import {
  ComponentPropsWithoutRef,
  useEffect,
  useRef,
  useState,
  ReactNode,
  Ref,
  forwardRef,
  KeyboardEvent,
  MouseEvent,
} from "react";
import cx from "classnames";

import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil, useMergeRefs } from "../../hooks";
import styles from "./ListView.module.scss";
import { listViewKeyboardNavigation } from "./utils";
import { ListViewOption } from "./ListViewOption";
import { ListViewOptionCell } from "./ListViewOptionCell";
import { mapOrder } from "../../internal";
import { ListViewContext } from "./ListViewContext";

/**
 * Represents an item in the listview with a label and optional disabled state.
 * @template T - The type of additional properties for the item
 */
type ItemType<T> = T & {
  /** The display label for the item */
  label: string;
  /** Whether the item is disabled and cannot be selected */
  disabled?: boolean;
};

/**
 * Props for the ListView component
 * @template T - The type of additional properties for items
 * @extends LayoutUtilProps
 */
type ListViewWithItems<T> = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> & {
  /** Array of items to render */
  items: ItemType<T>[];
  /** Default selected items (uncontrolled) */
  defaultSelected?: ItemType<T>[];
  /** Optional render function for items */
  children?: ({ items }: { items: ItemType<T>[] }) => ReactNode;
};

/**
 * Props for the ListView component without items array
 * @extends ComponentPropsWithoutRef<"div">
 */
type ListViewWithOutItems = ComponentPropsWithoutRef<"div"> & {
  /** Default selected items (uncontrolled) */
  defaultSelected?: string[];
};

/**
 * Option object for internal focus and selection management
 */
export type Option = {
  /** The DOM node for the option */
  node: Element;
  /** Focusable elements within the option */
  focusables: NodeListOf<Element>;
};

/**
 * Represents the focused item in the listview
 */
export type FocusedItem = {
  /** Row index of the focused item */
  row: number;
  /** Column index of the focused item */
  col: number;
};

/**
 * Props for the ListView component
 * @template T - The type of additional properties for items
 * @extends LayoutUtilProps
 */
export type ListViewProps<T> = LayoutUtilProps & {
  /** Currently indeterminate items (controlled) */
  indeterminate?: unknown[];
  /** Callback when selection changes */
  onSelectionChange?: (value: unknown[]) => void;
  /** Currently selected items (controlled) */
  selected?: ItemType<T>[];
} & (ListViewWithItems<T> | ListViewWithOutItems);

const getFocusables = (el: Element) => {
  return el.querySelectorAll(
    "button:not(:disabled):not([aria-disabled='true']), input:not(:disabled):not([aria-disabled='true']), [tabindex='0']:not(:disabled):not([aria-disabled='true'])",
  );
};

const ListViewElement = function <T>(
  props: ListViewProps<T>,
  ref: Ref<HTMLDivElement>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    onSelectionChange,
    className,
    selected,
    indeterminate,
    defaultSelected,
    style,
    onKeyDown,
    ...remainingProps
  } = componentProps;

  const ListViewRef = useRef<HTMLDivElement>(null);
  const isFocusVisible = useRef(true);
  const combinedRef = useMergeRefs([ref, ListViewRef]);
  const [options, setOptions] = useState<Option[]>();
  const [currentActive, setCurrentActive] = useState<string>();
  const [selectedItems, setSelectedItems] = useState<unknown[]>(
    selected ?? defaultSelected ?? [],
  );
  const [focusedItem, setFocusedItem] = useState<FocusedItem>({
    row: 0,
    col: 0,
  });

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  const onFocusHandler = () => {
    if (!isFocusVisible.current) return;
    if (!options) return;
    options[focusedItem.row].focusables[focusedItem.col].setAttribute(
      "data-interactive",
      "focus-visible",
    );
    return;
  };
  const onBlurHandler = () => {
    if (!options) return;
    options[focusedItem.row].focusables[focusedItem.col]?.removeAttribute(
      "data-interactive",
    );
    return;
  };

  const onKeyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    if (!options) return;
    isFocusVisible.current = true;
    listViewKeyboardNavigation(
      e,
      options,
      focusedItem,
      setFocusedItem,
      ListViewRef.current,
      (id) => setCurrentActive(id),
    );
  };

  const onMouseDownHandler = (e: MouseEvent<HTMLDivElement>) => {
    isFocusVisible.current = false;
    if (options && ListViewRef.current) {
      options[focusedItem.row].focusables[focusedItem.col]?.removeAttribute(
        "data-interactive",
      );

      const closestFocusable = (e.target as HTMLElement).closest(
        "button:not(:disabled):not([aria-disabled='true']), input:not(:disabled):not([aria-disabled='true']), [data-anv='checkbox']",
      );

      const optionRow = (e.target as HTMLElement).closest(
        '[data-anv="listview-option"]',
      );
      if (!optionRow || optionRow.ariaDisabled) return;
      const row = options.findIndex((option) => option.node === optionRow);
      if (!closestFocusable) {
        (options[row].focusables[0] as HTMLElement).click();
        return;
      }
      const col = Array.prototype.indexOf.call(
        options[row].focusables,
        closestFocusable?.getAttribute("data-anv") === "checkbox"
          ? closestFocusable.children[0]
          : closestFocusable,
      );
      setFocusedItem({ row, col });
    }
  };

  useEffect(() => {
    if (!ListViewRef.current) return;
    const optionArr = ListViewRef.current.querySelectorAll<HTMLDivElement>(
      "div[data-anv='listview-option']:not([aria-disabled='true'])",
    );
    const makeTree = Array.from(optionArr).map((option) => {
      const childFocusables = getFocusables(option);
      return {
        node: option,
        focusables: childFocusables,
      };
    });
    setOptions(makeTree);
  }, [props.children]);

  const prevSelectedItems = useRef<unknown[]>();
  useEffect(() => {
    if (!options) return;
    const compareArr = options.map((option) => option.node.ariaLabel);
    const orderedSelectedItems = mapOrder(selectedItems, compareArr, "label");
    if (
      JSON.stringify(prevSelectedItems.current) ===
      JSON.stringify(orderedSelectedItems)
    )
      return;

    onSelectionChange?.(orderedSelectedItems);
    prevSelectedItems.current = orderedSelectedItems;
  }, [onSelectionChange, options, selectedItems]);

  useEffect(() => {
    if (!selected) return;
    const filterDuplicates = selected.reduce(
      (acc: ListViewWithItems<T>["items"], current) => {
        const isDup = acc.find((item) => item.label === current.label);
        if (!isDup) {
          return acc.concat([current]);
        } else {
          return acc;
        }
      },
      [],
    );

    if (!options) return;
    const compareArr = options.map((option) => option.node.ariaLabel);
    const orderedSelected = mapOrder(filterDuplicates, compareArr, "label");
    if (
      JSON.stringify(prevSelectedItems.current) ===
      JSON.stringify(orderedSelected)
    )
      return;
    setSelectedItems(orderedSelected);
    prevSelectedItems.current = orderedSelected;
  }, [onSelectionChange, options, selected]);

  const ListViewClassNames = cx([styles["listview"]], className);

  if ("items" in remainingProps) {
    const { children, items, ...rest } = remainingProps;
    return (
      <ListViewContext.Provider
        value={{
          options,
          onSelectionChange,
          indeterminateItems: indeterminate ?? [],
          selectedItems,
          setSelectedItems,
          items,
          controlled: !!selected,
        }}
      >
        <div
          ref={combinedRef}
          className={ListViewClassNames}
          style={styleCombined}
          data-anv="listview"
          role="grid"
          aria-multiselectable="true"
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onKeyDown={onKeyDownHandler}
          onMouseDown={onMouseDownHandler}
          aria-activedescendant={currentActive}
          tabIndex={0}
          {...rest}
        >
          {children?.({
            items: items.map((item: ItemType<T>) => {
              return item;
            }),
          })}
        </div>
      </ListViewContext.Provider>
    );
  }

  const { children, ...rest } = remainingProps;
  return (
    <ListViewContext.Provider
      value={{
        options,
        onSelectionChange,
        selectedItems,
        indeterminateItems: indeterminate ?? [],
        setSelectedItems,
        controlled: !!selected || !!indeterminate,
      }}
    >
      <div
        ref={combinedRef}
        className={ListViewClassNames}
        style={styleCombined}
        data-anv="listview"
        role="grid"
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        onKeyDown={onKeyDownHandler}
        onMouseDown={onMouseDownHandler}
        aria-activedescendant={currentActive}
        tabIndex={0}
        {...rest}
      >
        {children}
      </div>
    </ListViewContext.Provider>
  );
};

ListViewElement.displayName = "ListView";

/**
 * ListView component for displaying a list of selectable items in a grid layout.
 *
 * Features:
 * - Supports single and multiple selection modes
 * - Keyboard navigation with arrow keys
 * - Customizable item rendering
 * - Controlled and uncontrolled modes
 * - Accessible with proper ARIA attributes
 * - Focus management for interactive elements
 * - Layout utilities for positioning and spacing
 *
 * @template T - The type of additional properties for items
 * @example
 * <ListView items={[{ label: "Item 1" }, { label: "Item 2" }]} onSelectionChange={handleSelection}>
 *   {({ items }) => items.map(item => (
 *     <ListView.Option key={item.label} item={item} />
 *   ))}
 * </ListView>
 */
export const ListView = Object.assign(
  forwardRef(ListViewElement) as <T>(
    props: ListViewProps<T> & { ref?: React.ForwardedRef<HTMLUListElement> },
  ) => ReturnType<typeof ListViewElement>,
  {
    /**
     * ListViewOption component for individual selectable items within a listview.
     *
     * Features:
     * - Displays selectable options with proper ARIA attributes
     * - Supports disabled state for non-selectable options
     * - Keyboard navigation support
     * - Accessible with screen reader support
     *
     * @example
     * <ListView.Option item={{ label: "Option 1" }} />
     */
    Option: ListViewOption,
    /**
     * ListViewOptionCell component for rendering a cell within a listview option.
     *
     * Features:
     * - Renders a grid cell for option content
     * - Supports custom content and layout
     * - Accessible with proper ARIA attributes
     *
     * @example
     * <ListView.OptionCell>Custom cell content</ListView.OptionCell>
     */
    OptionCell: ListViewOptionCell,
  },
);
